import { queryOptions } from '@tanstack/react-query'

import { STATS_REFETCH_MS } from '@/shared/config'

import type { StatsGranularity } from '../model/types'

import {
  fetchCampaign,
  fetchCampaignFilters,
  fetchCampaignCount,
  fetchCampaignReport,
  fetchCampaigns,
  fetchCampaignSources,
} from './campaigns-api'
import { campaignKeys } from './query-keys'

export const campaignsListQuery = () =>
  queryOptions({
    queryKey: campaignKeys.list(),
    queryFn: ({ signal }) => fetchCampaigns(signal),
  })

export const campaignCountQuery = (userId?: string) =>
  queryOptions({
    queryKey: campaignKeys.count(userId),
    queryFn: ({ signal }) => fetchCampaignCount(userId, signal),
  })

export const campaignQuery = (id: string) =>
  queryOptions({
    queryKey: campaignKeys.detail(id),
    queryFn: ({ signal }) => fetchCampaign(id, signal),
  })

export const campaignSourcesQuery = (id: string) =>
  queryOptions({
    queryKey: campaignKeys.sources(id),
    queryFn: ({ signal }) => fetchCampaignSources(id, signal),
  })

export const campaignFiltersQuery = (id: string) =>
  queryOptions({
    queryKey: campaignKeys.filters(id),
    queryFn: ({ signal }) => fetchCampaignFilters(id, signal),
  })

/**
 * The totals for one window. The window is part of the key, so switching period
 * is a fetch rather than a refetch and the previous one stays cached.
 *
 * A counter with no stream behind it, so it polls: traffic arrives continuously
 * and nothing tells the panel about it. `staleTime: 0` because the number is
 * out of date the moment it is drawn, and `refetchInterval` because otherwise
 * the only thing that would refresh it is the reader switching windows and
 * switching back. The interval does not run in a hidden tab — that is
 * `refetchIntervalInBackground`, and it stays off.
 */
export const campaignReportQuery = (id: string, range: { from: string; to: string; granularity?: StatsGranularity }) =>
  queryOptions({
    queryKey: campaignKeys.report(id, range),
    staleTime: 0,
    refetchInterval: STATS_REFETCH_MS,
    queryFn: ({ signal }) => fetchCampaignReport(id, range, signal),
  })
